import { useEffect, useRef, useState } from 'react';
import {
  Plus,
  X,
  ChevronDown,
  Check,
  Trash2,
  MessagesSquare,
  Headset,
  ArrowUp,
  ImagePlus,
  Paperclip,
} from 'lucide-react';
import {
  IconButton,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@vnetwork/ui/primitives';
import type { UseLiveChat } from './use-livechat';
import styles from './LiveChatWidget.module.css';

const HEADER_LABEL: Record<'idle' | 'connecting' | 'live', string> = {
  idle: 'Support chat',
  connecting: 'Connecting…',
  live: 'Support chat',
};

function formatTime(t: number): string {
  return new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function LiveChatPanel({ chat, onClose }: { chat: UseLiveChat; onClose: () => void }) {
  const {
    conversations,
    activeId,
    messages,
    typing,
    status,
    agentName,
    send,
    sendImage,
    newConversation,
    selectConversation,
    deleteConversation,
  } = chat;

  const [input, setInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, typing]);

  const submit = (text: string) => {
    if (!text.trim()) return;
    send(text);
    setInput('');
  };

  const pickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    void sendImage(file).finally(() => setUploading(false));
  };

  const empty = messages.length === 0 && !typing;

  return (
    <div className={styles.window} role="dialog" aria-label="Live chat">
      <div className={styles.header}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={styles.switcher}>
              {HEADER_LABEL[status]}
              <ChevronDown size={14} color="var(--vn-fg-subtle)" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" style={{ minWidth: 240, maxWidth: 300 }}>
            <DropdownMenuLabel>Conversations</DropdownMenuLabel>
            {conversations.map((c) => (
              <DropdownMenuItem
                key={c.id}
                onSelect={() => selectConversation(c.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}
              >
                <MessagesSquare size={14} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: c.id === activeId ? 600 : 400,
                  }}
                >
                  {c.title}
                </span>
                {c.connected ? (
                  <Badge tone="success" appearance="subtle" size="sm">
                    Live
                  </Badge>
                ) : null}
                {c.id === activeId ? (
                  <Check size={14} style={{ flexShrink: 0, color: 'var(--vn-primary-text)' }} />
                ) : (
                  <span
                    role="button"
                    aria-label="Delete conversation"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteConversation(c.id);
                    }}
                    style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--vn-fg-subtle)', cursor: 'pointer' }}
                  >
                    <Trash2 size={13} />
                  </span>
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => newConversation()} style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
              <Plus size={14} /> New conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className={styles.headerActions}>
          {/* New-conversation lives inside the switcher dropdown above, where the
              existing conversations stay visible — so starting a fresh chat never
              looks like the previous one was lost. */}
          <IconButton aria-label="Close chat" variant="ghost" size="sm" onClick={onClose}>
            <X size={16} />
          </IconButton>
        </div>
      </div>

      <div className={styles.body}>
        {empty ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>
              <Headset size={28} />
            </span>
            <h2 className={styles.emptyTitle}>Chat with our team</h2>
            <p className={styles.emptyText}>
              Send a message and a support agent will be with you shortly.
            </p>
          </div>
        ) : (
          <div className={styles.thread}>
            {messages.map((m) => {
              // Image/file attachment renders in place of a text bubble.
              const attachment =
                m.kind === 'image' && m.fileUrl ? (
                  <a
                    href={m.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.imageAttachment}
                  >
                    <img src={m.fileUrl} alt={m.fileName ?? 'Image'} loading="lazy" />
                  </a>
                ) : m.kind === 'file' && m.fileUrl ? (
                  <a
                    href={m.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.fileAttachment}
                  >
                    <Paperclip size={14} />
                    {m.fileName ?? 'File'}
                  </a>
                ) : null;

              if (m.role === 'user') {
                return (
                  <div key={m.id} className={styles.rowUser}>
                    {attachment ?? <div className={styles.userBubble}>{m.content}</div>}
                    <span className={styles.time}>{formatTime(m.time)}</span>
                  </div>
                );
              }
              if (m.role === 'system') {
                return (
                  <div key={m.id} className={styles.systemNote}>
                    {m.content}
                  </div>
                );
              }
              // agent (human)
              return (
                <div key={m.id} className={styles.rowAgent}>
                  <span className={styles.avatar}>{(m.agentName ?? 'A').charAt(0).toUpperCase()}</span>
                  <div className={styles.agentSide}>
                    {m.agentName ? <span className={styles.agentName}>{m.agentName}</span> : null}
                    {attachment ?? <div className={styles.agentBubble}>{m.content}</div>}
                    <span className={styles.time}>{formatTime(m.time)}</span>
                  </div>
                </div>
              );
            })}
            {typing ? (
              <div className={styles.rowAgent}>
                <span className={styles.avatar}>{(agentName ?? 'A').charAt(0).toUpperCase()}</span>
                <div className={styles.agentBubble}>
                  <span className={styles.typing}>
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                  </span>
                </div>
              </div>
            ) : null}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.composer}>
          <textarea
            placeholder="Type your message…"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Skip Enter while an IME is composing (e.g. Vietnamese input on
              // macOS) — the keydown fires to confirm the character and would
              // otherwise double-submit the message.
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit(input);
              }
            }}
            className={styles.composerInput}
          />
          <div className={styles.composerRow}>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={pickImage}
            />
            <IconButton
              aria-label="Attach image"
              variant="ghost"
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <ImagePlus size={16} />
            </IconButton>
            <span className={styles.composerSend}>
              <IconButton aria-label="Send" variant="primary" size="sm" onClick={() => submit(input)}>
                <ArrowUp size={15} />
              </IconButton>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
